// Webflow Form widget (.w-form): a <form> plus success (.w-form-done) and error (.w-form-fail)
// messages. Webflow's hosted form backend is gone, so a form with a `hubspot` form submits
// to that HubSpot form (see @lib/hubspot). A form without one (the partners filter) never
// submits anywhere.

import { createContext, useContext, useState } from 'react';
import type { FormHTMLAttributes } from 'react';
import { submitToHubspot } from '@lib/hubspot';
import type { HubspotForm } from '@lib/hubspot';
import type { WfProps } from './element';

type Status = 'idle' | 'submitting' | 'done' | 'fail';

const FormContext = createContext<{ status: Status; setStatus: (s: Status) => void } | null>(null);

export function WebflowForm({ as: Tag = 'div', children, ...props }: WfProps) {
  const [status, setStatus] = useState<Status>('idle');
  return (
    <FormContext.Provider value={{ status, setStatus }}>
      <Tag {...props}>{children}</Tag>
    </FormContext.Provider>
  );
}

export function FormElement({
  hubspot,
  children,
  style,
  ...props
}: FormHTMLAttributes<HTMLFormElement> & Record<`data-${string}`, string> & { hubspot?: HubspotForm }) {
  const form = useContext(FormContext);
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!form || !hubspot || form.status === 'submitting') return;
    const el = e.currentTarget;
    const submit = el.querySelector<HTMLInputElement>('input[type=submit][data-wait]');
    const label = submit?.value;
    if (submit) submit.value = submit.dataset.wait ?? label ?? '';
    form.setStatus('submitting');
    try {
      await submitToHubspot(hubspot, new FormData(el));
      form.setStatus('done');
    } catch (err) {
      console.error('Form submission failed:', err);
      form.setStatus('fail');
    } finally {
      if (submit && label !== undefined) submit.value = label;
    }
  };
  return (
    <form
      {...props}
      // Stops HubSpot's tracking script from also capturing the form as a "non-HubSpot
      // form", which would record each submission twice.
      {...(hubspot && { 'data-hs-do-not-collect': 'true' })}
      style={form?.status === 'done' ? { ...style, display: 'none' } : style}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );
}

export function FormDone({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const form = useContext(FormContext);
  const shown = form?.status === 'done';
  return (
    <Tag
      {...props}
      tabIndex={-1}
      role="region"
      aria-label={props['aria-label'] ?? 'Form success'}
      style={shown ? { ...style, display: 'block' } : style}
    >
      {children}
    </Tag>
  );
}

export function FormFail({ as: Tag = 'div', style, children, ...props }: WfProps) {
  const form = useContext(FormContext);
  const shown = form?.status === 'fail';
  return (
    <Tag
      {...props}
      tabIndex={-1}
      role="region"
      aria-label={props['aria-label'] ?? 'Form failure'}
      style={shown ? { ...style, display: 'block' } : style}
    >
      {children}
    </Tag>
  );
}
